import React, { useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { createGlobalStyle } from 'styled-components';
import { spacing, themeLight } from '@naf/theme';
import { IconButton } from '@naf/icon-button';
import Slider from 'react-slick';
import { BaseCaption, StyledNextArrow, StyledPrevArrow } from './Styles';
import { isAdvancedImage } from './image-carousel';
const GlobalStyle = createGlobalStyle(["html{", "}"], ({
  open
}) => open && css(["overflow:hidden;"]));
export const ImageModal = ({
  open = false,
  initialIndex = 0,
  onClose,
  images = [],
  trapFocus = true,
  className
}) => {
  const modalSliderRef = useRef(null);
  const handleKeyDown = useCallback(e => {
    const slider = modalSliderRef && modalSliderRef.current;
    if (e.key === 'ArrowRight' && slider) {
      slider.slickNext();
    }
    if (e.key === 'ArrowLeft' && slider) {
      slider.slickPrev();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  }, [modalSliderRef, onClose]);
  useEffect(() => {
    window.addEventListener('keydown', e => {
      handleKeyDown(e);
    });
    return () => window.removeEventListener('keydown', e => {
      handleKeyDown(e);
    });
  }, [handleKeyDown]);
  useEffect(() => {
    if (trapFocus) {
      const element = document.querySelector('.modal');
      if (element) {
        let focusables = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]');
        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        firstFocusable.focus();
        const trapFocus = e => {
          if (e.key === 'Tab') {
            if (e.shiftKey && document.activeElement === firstFocusable) {
              /* shift + tab */
              lastFocusable.focus();
              e.preventDefault();
            } else if (document.activeElement === lastFocusable && !e.shiftKey) {
              /* tab */
              firstFocusable.focus();
              e.preventDefault();
            }
          }
        };
        document.addEventListener('keydown', trapFocus);
        return () => document.removeEventListener('keydown', trapFocus);
      }
      return () => {};
    }
    return () => {};
  }, [trapFocus, open]);
  if (!open) return null;
  return /*#__PURE__*/React.createElement(Outer, {
    className: `${className} modal`
  }, /*#__PURE__*/React.createElement(GlobalStyle, {
    open: open
  }), /*#__PURE__*/React.createElement(Slider, {
    infinite: true,
    speed: 500,
    slidesPerRow: 1,
    prevArrow: images.length > 1 ? /*#__PURE__*/React.createElement(StyledPrevArrow, null) : undefined,
    nextArrow: images.length > 1 ? /*#__PURE__*/React.createElement(StyledNextArrow, null) : undefined,
    adaptiveHeight: true,
    ref: modalSliderRef,
    initialSlide: initialIndex || 0
  }, images.map(image => {
    const advancedImage = isAdvancedImage(image);
    return /*#__PURE__*/React.createElement(Image, {
      key: image.description
    }, !advancedImage ? /*#__PURE__*/React.createElement("img", {
      alt: image.altText,
      src: image.srcFull ?? image.src
    }) : image.image, /*#__PURE__*/React.createElement(ModalCaption, null, image.description));
  })), /*#__PURE__*/React.createElement(CloseButton, {
    onClick: onClose,
    icon: "close",
    ariaLabel: "Lukk",
    variant: "secondary-inverted"
  }));
};

// slideshow
const Outer = styled.div.withConfig({
  displayName: "image-modal__Outer",
  componentId: "sc-cnpgsp-0"
})(["position:fixed;top:0;left:0;bottom:0;width:100vw;height:100%;background:", ";z-index:9999;animation:y 0.25s;@keyframes y{from{transform:translateY(100%);}to{transform:translateY(0%);}}&#mousedown *:focus{outline:0;}"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default);
const imageStyle = css(["max-height:95vh;object-fit:contain;max-width:100vw;width:100%;height:100%;"]);
const CloseButton = styled(IconButton).withConfig({
  displayName: "image-modal__CloseButton",
  componentId: "sc-cnpgsp-1"
})(["position:absolute;top:20px;right:20px;"]);
const Image = styled.div.withConfig({
  displayName: "image-modal__Image",
  componentId: "sc-cnpgsp-2"
})(["position:relative;height:100vh;display:flex !important;justify-content:center;align-items:center;& img{", "}"], imageStyle);
const ModalCaption = styled(BaseCaption).withConfig({
  displayName: "image-modal__ModalCaption",
  componentId: "sc-cnpgsp-3"
})(["bottom:", ";position:absolute;max-width:50vw;"], spacing.space32);
//# sourceMappingURL=image-modal.js.map